import React from 'react';
import { Link, To, useLocation } from 'react-router-dom';

import Section from '@hero/ui-kit/layout/Section';
import Loading from '../../../../../components/States/Loading';
import getMemberUrl from '../../../utils/getMemberUrl';
import { useOrganizationMemberDetailsContext } from '../../context/OrganizationMemberDetailsContext';
import useGetMemberClinicalData from '../../EhrRecord/api/useGetMemberClinicalData';
import StatusPill from '../../EhrRecord/components/StatusPill';
import { allergyStatusTone, clinicalStatusTone } from '../../EhrRecord/components/StatusPill/tones';
import {
    getTopAllergies,
    getTopConditions,
    hasEhrRecord
} from '../../EhrRecord/utils/clinicalSummary';
import { formatAllergyName, formatEnumLabel } from '../../EhrRecord/utils/formatters';
import * as Style from './style.module.scss';

interface SummaryRow {
    id: number;
    name: string;
    detail?: string | null;
    status: string | null;
}

interface SummaryGroupProps {
    title: string;
    rows: SummaryRow[];
    total: number;
    emptyMessage: string;
    tone: (status: string | null) => ReturnType<typeof clinicalStatusTone>;
    /** Where "+N more" leads, with the router state to carry. */
    moreTo: To;
    moreState: unknown;
}

const SummaryGroup: React.FC<SummaryGroupProps> = ({
    title,
    rows,
    total,
    emptyMessage,
    tone,
    moreTo,
    moreState
}) => (
    <div className={Style.group}>
        <span className={Style.label}>{title}</span>
        {rows.length ? (
            <ul className={Style.list}>
                {rows.map((row) => (
                    <li key={row.id} className={Style.row}>
                        <span className={Style.name}>
                            {row.name}
                            {row.detail ? (
                                <span className={Style.detail}>{` ${row.detail}`}</span>
                            ) : null}
                        </span>
                        <StatusPill label={formatEnumLabel(row.status)} tone={tone(row.status)} />
                    </li>
                ))}
            </ul>
        ) : (
            <span className={Style.empty}>{emptyMessage}</span>
        )}
        {total > rows.length ? (
            <Link to={moreTo} state={moreState} className={Style.more}>
                {`+${total - rows.length} more`}
            </Link>
        ) : null}
    </div>
);

/** Top conditions and allergies from the EHR record, linking to the full tab. */
const ClinicalSummary: React.FC = () => {
    const { memberDetails } = useOrganizationMemberDetailsContext();
    const { state } = useLocation();
    const memberInfo = memberDetails?.member_info;

    const { data, isLoading, isError } = useGetMemberClinicalData(memberInfo?.member_id);

    if (isLoading) {
        return (
            <Section border>
                <Loading height={8} />
            </Section>
        );
    }

    if (isError || !hasEhrRecord(data)) return null;

    // Same state the tab links carry, so the list back-link and prev/next survive.
    const more = {
        moreTo: getMemberUrl(memberInfo?.hero_user_id, memberInfo?.member_id, 'ehr'),
        moreState: state
    };
    const conditions = getTopConditions(data.conditions);
    const allergies = getTopAllergies(data.allergies);

    return (
        <Section border noDefaultPadding className={Style.card}>
            <SummaryGroup
                title="Conditions"
                rows={conditions.items.map((condition) => ({
                    id: condition.id,
                    name: condition.name || 'Unnamed',
                    status: condition.status
                }))}
                total={conditions.total}
                emptyMessage="No conditions on file."
                tone={clinicalStatusTone}
                {...more}
            />
            <SummaryGroup
                title="Allergies"
                rows={allergies.items.map((allergy) => ({
                    id: allergy.id,
                    name: formatAllergyName(allergy),
                    detail: allergy.reaction,
                    status: allergy.status
                }))}
                total={allergies.total}
                emptyMessage="No known allergies recorded."
                tone={allergyStatusTone}
                {...more}
            />
        </Section>
    );
};

export default ClinicalSummary;
